import { IAdSectionInfo, TAdSlotTemplate } from '@/models/Search/interfaces/IAdSectionInfo';
import {
  EN_CELL,
  IHeader,
  EN_HEADER_CELL,
  GroupNameNormal,
  GroupNameSorted,
  ISearchListPolicyGroupItemCellTypeFunction,
  ISearchListPolicyGroupByFunction,
  ISearchListPolicyGroupHeaderFunction,
  ISearchListPolicyGroupOrderFunction,
} from '@/models/Search/policies/ISearchListPolicy';
import { compose, assoc, prop, map } from 'ramda';

export const findAdSection = (adSections: IAdSectionInfo[], id: string): IAdSectionInfo | undefined =>
  adSections.find((ad: IAdSectionInfo) => ad.adSlotId === id);

export const addGeneralGroupIds = (ids: string[]): string[] => ids.concat([GroupNameNormal, GroupNameSorted]);
export const extractOrderMap = compose(
  (ids) => ids.reduce((obj, id, idx) => assoc(id, idx)(obj), {}),
  addGeneralGroupIds,
  map<IAdSectionInfo, string>(prop('adSlotId')),
);

const toCellType = (tpl: TAdSlotTemplate): EN_CELL | undefined =>
  ({
    banner: EN_CELL.SMALL_BANNER,
    normal: EN_CELL.TEXT,
  }[tpl]);

export const adGroupCellType = (ad: IAdSectionInfo): EN_CELL =>
  toCellType(ad.adSlotTemplate) ?? toCellType(ad.adSlotDefaultTemplate) ?? EN_CELL.NONE;

export const adGroupHeader = (ad: IAdSectionInfo, index: number): IHeader => ({
  adSlotId: ad.adSlotId,
  index,
  title: ad.adSlot,
  type: EN_HEADER_CELL.TEXT,
  adTooltip: {
    available: true,
    desc: ad.adSlotTooltip,
  },
});

interface IGroupManagerParams {
  generalHeader: ISearchListPolicyGroupHeaderFunction;
}

export interface IGroupManager {
  createGroupByFunction: () => ISearchListPolicyGroupByFunction;
  createGroupOrderFunction: (adSections: IAdSectionInfo[]) => ISearchListPolicyGroupOrderFunction;
  createGroupHeaderFunction: (adSections: IAdSectionInfo[]) => ISearchListPolicyGroupHeaderFunction;
  createGroupItemCellTypeFunction: (adSections: IAdSectionInfo[]) => ISearchListPolicyGroupItemCellTypeFunction;
}

export const createGroupManager = ({ generalHeader }: IGroupManagerParams): IGroupManager => {
  return {
    createGroupByFunction: () => (item): string => item.adSlotId ?? GroupNameNormal,
    createGroupOrderFunction: (adSections) => ([groupName]) => extractOrderMap(adSections)[groupName],
    createGroupHeaderFunction: (adSections) => (groupName, index) => {
      const adSection = findAdSection(adSections, groupName);
      return adSection ? adGroupHeader(adSection, index) : generalHeader(groupName, index);
    },
    createGroupItemCellTypeFunction: (adSections) => (group) => {
      const adSection = findAdSection(adSections, group[0]);
      return adSection ? adGroupCellType(adSection) : EN_CELL.TEXT;
    },
  };
};
